import React from 'react';

import {useHistory, useLocation} from 'react-router';
import qs from 'query-string';
import {useLocalizationContext} from '@epic-core/localization';

/**
 * useHideLocaleIfPreference removes the lang URL query parameter
 * if the current parameter matches with the first supported locale
 * of the user's language preference. The query param should only show if
 * it deviates from the user's (first supported) preference
 */
export function useHideLocaleIfPreference(languagePreference: string[]) {
  const {supportedLocales} = useLocalizationContext();
  const location = useLocation();
  const history = useHistory();

  if (!languagePreference) {
    throw new Error('useHideLocaleIfPreference needs a `languagePreference` argument');
  }

  React.useEffect(() => {
    const queryParams = qs.parse(location.search);
    if (queryParams.lang && languagePreference.length > 0) {
      for (const lang of languagePreference) {
        for (const supportedLanguage of supportedLocales) {
          const isSupportedLanguagePreference =
            lang === supportedLanguage.lang ||
            (supportedLanguage.lang === 'en-US' && lang.includes('en'));

          if (isSupportedLanguagePreference) {
            if (queryParams.lang === supportedLanguage.lang) {
              // first supported lang preference matches the query param, remove it
              history.replace({
                ...location,
                search: qs.stringify({...queryParams, lang: undefined}),
              });
            }
            // otherwise the first supported language deviates from the query param,
            // so we leave the query parameter in place and are done

            // in both scenario's we're done so return to exit all loops
            // and stop executing this function
            return;
          }
        }
      }
    }
  }, [location.search, supportedLocales, languagePreference]);
}
